import type { App, Plugin } from 'vue'

// Base components
import AM3Avatar from './base/AM3Avatar.vue'
import AM3Button from './base/AM3Button.vue'
import AM3DropdownMenu from './base/AM3DropdownMenu.vue'
import AM3DropdownPanel from './base/AM3DropdownPanel.vue'
import AM3Loading from './base/AM3Loading.vue'
import AM3LoadingContent from './base/AM3LoadingContent.vue'
import AM3SplitButton from './base/AM3SplitButton.vue'
import AM3TagList from './base/AM3TagList.vue'
import AM3Tag from './base/AM3Tag.vue'
import AM3UserCountTag from './base/AM3UserCountTag.vue'
import AM3FlagIcon from './base/icon/AM3FlagIcon.vue'
import AM3Icon from './base/icon/AM3Icon.vue'

// Data components
import AM3CalendarItem from './data/AM3CalendarItem.vue'
import AM3ColumnEdit from './data/AM3ColumnEdit.vue'
import AM3DataList from './data/AM3DataList.vue'
import AM3FloatingActionPanel from './data/AM3FloatingActionPanel.vue'
import AM3Formatter from './data/AM3Formatter.vue'
import AM3Pagination from './data/AM3Pagination.vue'

// Feedback components
import AM3SnackbarContainer from './feedback/AM3SnackbarContainer.vue'
import AM3Snackbar from './feedback/AM3Snackbar.vue'
import AM3Badge from './base/AM3Badge.vue'
import AM3DashedBorder from './base/AM3DashedBorder.vue'

// Input components
import AM3InputError from './inputs/AM3InputError.vue'
import AM3InputLabel from './inputs/AM3InputLabel.vue'

// Advanced input components
import AM3InputCountry from './inputs/advanced/AM3InputCountry.vue'
import AM3InputMobile from './inputs/advanced/AM3InputMobile.vue'
import AM3InputTags from './inputs/advanced/AM3InputTags.vue'

// Entry input components
import AM3InputDate from './inputs/entry/AM3InputDate.vue'
import AM3InputHeading from './inputs/entry/AM3InputHeading.vue'
import AM3InputNumber from './inputs/entry/AM3InputNumber.vue'
import AM3InputSelect from './inputs/entry/AM3InputSelect.vue'
import AM3InputSlug from './inputs/entry/AM3InputSlug.vue'
import AM3InputText from './inputs/entry/AM3InputText.vue'
import AM3InputFile from './inputs/entry/AM3InputFile.vue'
import AM3InputTextArea from './inputs/entry/AM3InputTextarea.vue'
import AM3InputTextAreaWysiwyg from './inputs/entry/AM3InputTextareaWysiwyg.vue'

// Selection input components
import AM3Checkbox from './inputs/selection/AM3Checkbox.vue'
import AM3Radio from './inputs/selection/AM3Radio.vue'
import AM3ToggleButton from './inputs/selection/AM3ToggleButton.vue'
import AM3Toggle from './inputs/selection/AM3Toggle.vue'
import AM3Selectbox from './inputs/selection/AM3Selectbox.vue'

// Layout components
import AM3Accordion from './layout/AM3Accordion.vue'
import AM3Collapsible from './layout/AM3Collapsible.vue'
import AM3Modal from './layout/AM3Modal.vue'
import AM3CreateHeader from './layout/AM3CreateHeader.vue'
import AM3CreateLayout from './layout/AM3CreateLayout.vue'
import AM3StepLink from './layout/AM3StepLink.vue'
import AM3Stepper from './layout/AM3Stepper.vue'
import AM3Tabs from './layout/AM3Tabs.vue'

// Define component types to avoid experimental warning
type ComponentType = any

// List of all components with explicit type
const components: Record<string, ComponentType> = {
  AM3Avatar,
  AM3Button,
  AM3DropdownMenu,
  AM3DropdownPanel,
  AM3Loading,
  AM3LoadingContent,
  AM3SplitButton,
  AM3TagList,
  AM3Tag,
  AM3UserCountTag,
  AM3FlagIcon,
  AM3Icon,
  AM3CalendarItem,
  AM3ColumnEdit,
  AM3DataList,
  AM3FloatingActionPanel,
  AM3Formatter,
  AM3Pagination,
  AM3SnackbarContainer,
  AM3Snackbar,
  AM3Badge,
  AM3DashedBorder,
  AM3InputError,
  AM3InputLabel,
  AM3InputCountry,
  AM3InputMobile,
  AM3InputTags,
  AM3InputDate,
  AM3InputHeading,
  AM3InputNumber,
  AM3InputSelect,
  AM3InputSlug,
  AM3InputText,
  AM3InputTextArea,
  AM3InputTextAreaWysiwyg,
  AM3InputFile,
  AM3Checkbox,
  AM3Selectbox,
  AM3Radio,
  AM3ToggleButton,
  AM3Toggle,
  AM3Accordion,
  AM3Collapsible,
  AM3Modal,
  AM3CreateHeader,
  AM3CreateLayout,
  AM3StepLink,
  AM3Stepper,
  AM3Tabs,
}

// Create plugin with proper typings
const AM3ComponentsPlugin: Plugin = {
  install(app: App) {
    // Register each component globally
    Object.entries(components).forEach(([name, component]) => {
      // For HMR to work properly, we need to ensure components have __hmrId
      if ((component as any).__file && !(component as any).__hmrId) {
        (component as any).__hmrId = (component as any).__file
      }
      app.component(name, component)
    })
  },
}

export default AM3ComponentsPlugin

// Also export each component individually for convenience
export {
  AM3Avatar,
  AM3Button,
  AM3DropdownMenu,
  AM3DropdownPanel,
  AM3Loading,
  AM3LoadingContent,
  AM3SplitButton,
  AM3TagList,
  AM3Tag,
  AM3UserCountTag,
  AM3FlagIcon,
  AM3Icon,
  AM3CalendarItem,
  AM3ColumnEdit,
  AM3DataList,
  AM3FloatingActionPanel,
  AM3Formatter,
  AM3Pagination,
  AM3SnackbarContainer,
  AM3Snackbar,
  AM3Badge,
  AM3DashedBorder,
  AM3InputError,
  AM3InputLabel,
  AM3InputCountry,
  AM3InputMobile,
  AM3InputTags,
  AM3InputDate,
  AM3InputHeading,
  AM3InputNumber,
  AM3InputSelect,
  AM3InputSlug,
  AM3InputText,
  AM3InputTextArea,
  AM3InputTextAreaWysiwyg,
  AM3InputFile,
  AM3Checkbox,
  AM3Radio,
  AM3ToggleButton,
  AM3Toggle,
  AM3Accordion,
  AM3Collapsible,
  AM3Modal,
  AM3CreateHeader,
  AM3CreateLayout,
  AM3StepLink,
  AM3Stepper,
  AM3Tabs,
  AM3Selectbox,
}
